import type { SelectableColumn } from "@/hooks/use-column-selection";
import { useAccountColumnSelection } from "@/hooks/use-column-selection";
import { getAccountColumns } from "@/pages/accounts/account-list/account-columns";
import { EntityColumnSelector } from "./entity-column-selector";

interface AccountsColumnSelectorProps {
  // Optional: provide column state externally (for DRR integration)
  columnState?: {
    availableColumns: SelectableColumn[];
    isColumnSelected: (columnId: string) => boolean;
    toggleColumn: (columnId: string) => void;
    isLoading?: boolean;
    error?: Error | null;
  };
}

/**
 * Component for managing account column selection including both core and custom fields.
 * Displays a popover with checkboxes for selecting which columns to display.
 * Can accept external column state for DRR integration, or use its own hooks.
 */
export function AccountsColumnSelector({
  columnState,
}: AccountsColumnSelectorProps = {}) {
  // Get the core columns
  const coreColumns = getAccountColumns();

  // Use provided state OR internal hook
  const internalState = useAccountColumnSelection(
    columnState ? [] : coreColumns,
  );
  const { availableColumns, isColumnSelected, toggleColumn, isLoading, error } =
    columnState || internalState;

  return (
    <EntityColumnSelector
      availableColumns={availableColumns}
      isColumnSelected={isColumnSelected}
      toggleColumn={toggleColumn}
      isLoading={isLoading || false}
      error={error || null}
      buttonLabel="Columns"
      noColumnsMessage="No columns available."
      errorMessage="Error loading columns."
    />
  );
}
